import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AllowanceSpentResponse, NamedItemString, PlanPeriodDto, getAllowanceSpent } from 'core'

import { Card, SimpleSelect, Skeleton } from 'common/components'
import { getMonths } from 'common/dateFormatters'
import { useQueryRequestV2 } from 'common/hooks'
import { formatMoney } from 'common/moneyFormatter'

import AllowanceStatistic from '../../../common/allowanceStatistic'

type AllowanceSpentProps = {
  planPeriod: PlanPeriodDto
}

type StatsProps = {
  amount: {
    spent: string
    total: string
  }
  innerPie: {
    title: string
    content: string
  }
  trend?: {
    increasing: boolean
    value: number
  }
}

const AllowanceSpent = ({ planPeriod, ...props }: AllowanceSpentProps) => {
  const { t: translate } = useTranslation()
  const { isLoading, sendQuery } = useQueryRequestV2()

  const [selectedDate, setSelectedDate] = useState<string>(null)
  const [availableDateOptions, setAvailableDateOptions] = useState<NamedItemString[]>([])
  const [stats, setStats] = useState<StatsProps>({
    amount: {
      spent: '',
      total: '',
    },
    innerPie: {
      title: '',
      content: '0',
    },
  })

  useEffect(() => {
    if (!planPeriod) return

    const months = getMonths(planPeriod.startDate, planPeriod.endDate).reverse()
    setAvailableDateOptions([{ id: null, name: 'All' }, ...months])
  }, [planPeriod])

  useEffect(() => {
    ;(async () => {
      const result = await sendQuery(() => getAllowanceSpent(planPeriod.id, selectedDate))

      const { allowanceUsed, percentageIncrease, totalAllowance, percentageUsed } = result as AllowanceSpentResponse

      setStats({
        ...stats,
        amount: {
          spent: formatMoney(allowanceUsed),
          total: formatMoney(totalAllowance),
        },
        innerPie: {
          title: '',
          content: `${percentageUsed}`,
        },
        trend: {
          increasing: percentageIncrease > -1,
          value: percentageIncrease,
        },
      })
    })()
  }, [selectedDate, planPeriod])

  return (
    <Card className="h-100 t-allowance-spent">
      <div className="">
        <div className="mb-16 flex jc-space-between align-center">
          <h5 className="mb-0">{translate('admin-dashboard.home.reports.allowance.title')}</h5>

          <div style={{ minWidth: 100 }}>
            <SimpleSelect
              className={'t-month-drop-down-filter'}
              value={selectedDate}
              items={availableDateOptions}
              onChange={(value) => setSelectedDate(value)}
            />
          </div>
        </div>

        <Skeleton loading={isLoading}>
          <AllowanceStatistic
            data={[
              {
                type: 'Allowance Spent',
                value: parseFloat(stats.innerPie.content),
                color: '#14d273',
              },
            ]}
            {...stats}
            width={115}
            height={115}
          />
        </Skeleton>
      </div>
    </Card>
  )
}

export default AllowanceSpent
